import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Cart } from '../models/cart.model';
import { Product } from '../models/product.model';
import { StorefrontOrder } from '../models/order.model';
import {
  META_PIXEL_CURRENCY,
  buildCartContentIds,
  buildCartContents,
  buildCartNumItems,
  buildCartValue,
  buildPurchaseInputFromCart,
  buildPurchaseInputFromSummary,
  getProductUnitPrice,
} from './meta-pixel.helpers';
import { resolveMerchantId } from './merchant-id.util';
import { MetaPixelEventPayload, MetaPixelPurchaseInput, MetaPixelStandardEvent } from './meta-pixel.types';

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: FbqFunction;
};

@Injectable({
  providedIn: 'root',
})
export class MetaPixelService {
  private readonly pixelId = (environment.metaPixelId ?? '').trim();
  private scriptRequested = false;
  private initialized = false;
  private networkAllowed = false;
  private readonly queuedEvents: Array<() => void> = [];
  private readonly purchaseDedupePrefix = 'zt.meta.purchase.';

  /** Avoid duplicate InitiateCheckout when checkout form re-renders. */
  private checkoutTrackedForSession = false;

  get isEnabled(): boolean {
    return !!this.pixelId;
  }

  init(): void {
    if (!this.isEnabled || typeof window === 'undefined' || !this.networkAllowed) {
      return;
    }

    this.ensurePixelScript();
    if (!window.fbq || this.initialized) {
      return;
    }

    window.fbq('init', this.pixelId);
    this.initialized = true;
  }

  /** Load fbevents.js after LCP settle / first gesture so it cannot steal LCP/TBT. */
  allowNetwork(): void {
    if (this.networkAllowed) {
      return;
    }
    this.networkAllowed = true;
    this.init();
    const queued = this.queuedEvents.splice(0);
    for (const run of queued) {
      run();
    }
  }

  trackPageView(): void {
    this.track('PageView');
  }

  trackViewContent(product: Product): void {
    const unitPrice = getProductUnitPrice(product);
    this.track('ViewContent', {
      content_ids: [resolveMerchantId(product)],
      content_name: product.name,
      content_type: 'product',
      value: unitPrice,
      currency: META_PIXEL_CURRENCY,
      contents: [{ id: resolveMerchantId(product), quantity: 1, item_price: unitPrice }],
    });
  }

  trackAddToCart(product: Product, quantity: number): void {
    const unitPrice = getProductUnitPrice(product);
    const qty = Math.max(1, quantity);
    this.track('AddToCart', {
      content_ids: [resolveMerchantId(product)],
      content_name: product.name,
      content_type: 'product',
      value: unitPrice * qty,
      currency: META_PIXEL_CURRENCY,
      contents: [{ id: resolveMerchantId(product), quantity: qty, item_price: unitPrice }],
    });
  }

  trackInitiateCheckout(cart: Cart): void {
    if (this.checkoutTrackedForSession || !cart.cartItems?.length) {
      return;
    }

    this.checkoutTrackedForSession = true;
    this.track('InitiateCheckout', {
      value: buildCartValue(cart),
      currency: META_PIXEL_CURRENCY,
      num_items: buildCartNumItems(cart),
      content_ids: buildCartContentIds(cart),
      content_type: 'product',
      contents: buildCartContents(cart),
    });
  }

  resetCheckoutTracking(): void {
    this.checkoutTrackedForSession = false;
  }

  trackPurchase(input: MetaPixelPurchaseInput): void {
    const dedupeKey = String(input.orderId ?? input.orderNumber);
    if (!this.markPurchaseTracked(dedupeKey)) {
      return;
    }

    const contents = input.items.map((item) => ({
      id: item.merchantId || resolveMerchantId({ id: item.productId }),
      quantity: item.quantity,
      item_price: item.unitPrice,
    }));

    this.track('Purchase', {
      value: input.totalAmount,
      currency: META_PIXEL_CURRENCY,
      order_id: input.orderNumber,
      content_ids: contents.map((item) => item.id),
      content_type: 'product',
      contents,
      num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
    });
  }

  trackPurchaseFromCart(
    orderNumber: string,
    orderId: number | undefined,
    totalAmount: number,
    cart: Cart
  ): void {
    this.trackPurchase(buildPurchaseInputFromCart(orderNumber, orderId, totalAmount, cart));
  }

  trackPurchaseFromOrder(order: StorefrontOrder): void {
    const items = (order.orderItems ?? []).map((item) => ({
      productId: item.productId,
      merchantId: resolveMerchantId({
        id: item.productId,
        sku: item.product?.sku,
        merchantId: item.product?.merchantId,
      }),
      name: item.productName ?? item.product?.name ?? `Product #${item.productId}`,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    }));

    this.trackPurchase({
      orderId: order.id,
      orderNumber: order.orderNumber,
      totalAmount: order.totalAmount,
      items,
    });
  }

  trackPurchaseFromSummary(
    summary: {
      orderNumber: string;
      totalAmount: number;
      items: Array<{ name: string; quantity: number; totalPrice: number }>;
    },
    orderId?: number
  ): void {
    this.trackPurchase(buildPurchaseInputFromSummary(summary, orderId));
  }

  private track(event: MetaPixelStandardEvent, payload?: MetaPixelEventPayload): void {
    if (!this.isEnabled || typeof window === 'undefined') {
      return;
    }

    if (!this.networkAllowed) {
      this.queuedEvents.push(() => this.track(event, payload));
      return;
    }

    this.init();
    if (!window.fbq) {
      return;
    }

    if (payload) {
      window.fbq('track', event, payload);
      return;
    }

    window.fbq('track', event);
  }

  private ensurePixelScript(): void {
    if (this.scriptRequested || typeof document === 'undefined') {
      return;
    }

    if (window.fbq) {
      this.scriptRequested = true;
      return;
    }

    this.scriptRequested = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onerror = () => {
      this.scriptRequested = false;
    };
    document.head.appendChild(script);

    const fbq: FbqFunction = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
        return;
      }
      fbq.queue?.push(args);
    };
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    window.fbq = fbq;
    if (!window._fbq) {
      window._fbq = fbq;
    }
  }

  private markPurchaseTracked(key: string): boolean {
    if (!key || typeof sessionStorage === 'undefined') {
      return true;
    }

    const storageKey = `${this.purchaseDedupePrefix}${key}`;
    if (sessionStorage.getItem(storageKey)) {
      return false;
    }

    sessionStorage.setItem(storageKey, '1');
    return true;
  }
}
